/**
 * Voice formatting helpers.
 *
 * `formatPhoneNumber` was byte-identical in three screens; `formatDuration`
 * existed twice with DIFFERENT contracts under the same name — ActiveCalls
 * took a start time and computed against now, CallHistory took seconds. That
 * ambiguity is exactly the kind that silently produces a wrong number, so the
 * two are named apart here.
 */

/** +12345678901 → +1 (234) 567-8901 */
export const formatPhoneNumber = (phone, fallback = '—') => {
  if (!phone) return fallback;
  const cleaned = String(phone).replace(/\D/g, '');
  if (cleaned.length === 11) {
    return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

/** Seconds → m:ss, or h:mm:ss past an hour. */
export const formatSeconds = (seconds) => {
  const s = Number(seconds) || 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

/** Elapsed time since a start timestamp, as m:ss. */
export const formatElapsed = (startTime) => {
  if (!startTime) return '0:00';
  return formatSeconds(Math.floor((Date.now() - new Date(startTime).getTime()) / 1000));
};

/** Absolute timestamp for tables. */
export const formatTimestamp = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** "2m ago" / "3h ago" / a date past a week. */
export const formatRelative = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

/**
 * Pull a list out of a response body whose shape isn't consistent.
 *
 * The voice endpoints disagree about where a collection lives. GET
 * /api/sip/dids returns `{ success, dids, pagination }` with the array at the
 * TOP level; others nest under `data`, either as `data` itself or as
 * `data.<key>`. DIDManagement previously read `data.data?.dids` — matching
 * none of them, so the list was silently always empty — and the rewrite read
 * `data.data`, which assigned the wrapper OBJECT and threw
 * "dids.filter is not a function".
 *
 * Accepts every shape and guarantees an array: a list screen should degrade
 * to "empty" rather than crash the page.
 */
export const asArray = (body, key) => {
  const candidates = [body?.[key], body?.data?.[key], body?.data, body];
  return candidates.find(Array.isArray) || [];
};

/** Currency, defaulting to USD. */
export const formatCurrency = (amount, currency = 'USD') => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat([], { style: 'currency', currency }).format(n);
};
